import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import TenantList from './TenantList';
import CreateTenantDialog from './CreateTenantDialog';
import SuperAdminManager from './SuperAdminManager';
import { listTenants, listSuperAdmins } from '../../services/tenants';

// Task 16 (tenancy foundation): painel "Plataforma" -- primeira tela
// user-facing de todo o projeto de multi-tenancy. Gating desta rota é feito
// no App.js (SuperAdminRoute) e no menu lateral, mas isso é só conveniência
// de UI: a barreira de segurança real é o middleware `super.admin` na API
// (ver alodesk-chat-api routes/api.php) -- todos os requests aqui já
// assumem que só passam se a API aceitar.
const PlatformPage = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const [superAdmins, setSuperAdmins] = useState([]);
  const [loadingSuperAdmins, setLoadingSuperAdmins] = useState(true);

  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const data = await listTenants();
      setTenants(data);
    } catch (error) {
      toast.error('Não foi possível carregar as empresas.', { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  const loadSuperAdmins = useCallback(async () => {
    setLoadingSuperAdmins(true);
    try {
      const data = await listSuperAdmins();
      setSuperAdmins(data);
    } catch (error) {
      toast.error('Não foi possível carregar os super-admins.', { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setLoadingSuperAdmins(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { loadSuperAdmins(); }, [loadSuperAdmins]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          flexWrap: 'wrap',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.text.primary, lineHeight: 1.2 }}>
            Plataforma
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            {tab === 0
              ? 'Empresas (tenants) cadastradas na plataforma'
              : 'Quem tem acesso administrativo à plataforma inteira'}
          </Typography>
        </Box>
        {tab === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            Nova empresa
          </Button>
        )}
      </Box>

      <Box sx={{ px: '1.5rem', pb: '1.5rem', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 2,
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minHeight: 44,
              },
            }}
          >
            <Tab label="Empresas" />
            <Tab label="Super-admins" />
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2.5 }}>
            {tab === 0 ? (
              <TenantList tenants={tenants} loading={loadingTenants} onChanged={loadTenants} />
            ) : (
              <SuperAdminManager superAdmins={superAdmins} loading={loadingSuperAdmins} onChanged={loadSuperAdmins} />
            )}
          </Box>
        </Paper>
      </Box>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={loadTenants}
      />
    </Box>
  );
};

export default PlatformPage;
